import { useNavigate, useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { DesktopProductCard } from "../../components/desktop/product-card.tsx";
import { Empty, ErrorBlock, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { useCategories, useProducts } from "../../api/queries.ts";
import { useSpaces } from "../../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 全部商品
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **跟手机版是两种版面，不是同一份加断点。**
 *    手机版：品类横滑条 + 排序分段器 + 两列瀑布流（窄屏上只能这么排）。
 *    网页版：**左侧固定筛选栏 + 右侧等高网格** —— 有横向空间就该把筛选条件
 *    一直摆在眼前，而不是让人先滚回顶部才能换个品类。
 *
 * ⚠️ 筛选条件全部落在 URL 上：网页上用户会**开新标签页比价**、会存书签、
 *    会把链接发给同事。存在组件 state 里的话这些全断。
 */

const SORTS = [
  { key: "newest", label: "最新上架" },
  { key: "sales", label: "销量优先" },
  { key: "price_asc", label: "价格从低到高" },
  { key: "price_desc", label: "价格从高到低" },
] as const;

/*
  🔴 **这里原先自己写了一份中文的空间名，是个 bug。**
     筛选发出去的是 `space=客厅`，而商品上打的标签是 `living` ——
     这几个按钮点了**永远筛不出任何东西**，而且不报错，
     看起来就像"这个空间下没有货"。

     现在跟首页、案例、后台打标签共用同一份词表（后台配的那份），key 天然对得上。
*/

interface ProductSearch {
  categoryId?: number;
  space?: string;
  keyword?: string;
  sort?: string;
}

export function DesktopProducts() {
  const search = useSearch({ from: "/products" }) as ProductSearch;
  const navigate = useNavigate();

  const { data: categories } = useCategories();
  /** 🔴 空间词表全站一份，见 `lib/site-config.ts` 的 `useSpaces`。 */
  const spaces = useSpaces();
  const { data, isPending, error, refetch } = useProducts({
    categoryId: search.categoryId,
    space: search.space,
    keyword: search.keyword,
    sort: search.sort ?? "newest",
    limit: 40,
  });

  const setSearch = (next: Partial<ProductSearch>) =>
    navigate({ to: "/products", search: { ...search, ...next } as never });

  const activeCat = categories?.find((c) => c.id === search.categoryId);
  /** 当前生效的筛选，用来渲染可一键去掉的标签。 */
  const chips = [
    activeCat && { label: activeCat.name, clear: () => setSearch({ categoryId: undefined }) },
    /**
     * ⚠️ 标签上要出**中文名**不是 key。`space` 存的是 `kitchen` 这种标识，
     *    直接摆出去用户根本不知道自己筛的是什么。
     */
    search.space && {
      label: spaces.find((x) => x.key === search.space)?.label ?? search.space,
      clear: () => setSearch({ space: undefined }),
    },
    search.keyword && {
      label: `“${search.keyword}”`,
      clear: () => setSearch({ keyword: undefined }),
    },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  return (
    <DesktopShell>
      <Crumbs items={[{ label: "首页", to: "/" }, { label: "全部商品" }]} />

      <div className="grid grid-cols-[220px_minmax(0,1fr)] items-start gap-8">
        {/* ── 左：筛选栏 ── */}
        <aside className="sticky top-24 space-y-6 rounded-xl border border-border bg-card p-5">
          <div>
            <p className="mb-2.5 text-[13px] font-medium">品类</p>
            <div className="space-y-0.5">
              {[{ id: undefined as number | undefined, name: "全部品类" }, ...(categories ?? [])].map(
                (c) => {
                  const active = search.categoryId === c.id;
                  return (
                    <button
                      key={c.id ?? "all"}
                      onClick={() => setSearch({ categoryId: c.id })}
                      className={cn(
                        "block w-full rounded-md px-2.5 py-1.5 text-left text-[14px] transition-colors",
                        active
                          ? "bg-brand-50 font-medium text-brand-700"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                      )}
                    >
                      {c.name}
                    </button>
                  );
                },
              )}
            </div>
          </div>

          <div>
            {/*
              ⚠️ 「用在哪个空间」在手机版首页是五个大图标，这里做成筛选项 ——
                 网页上用户是带着"我要装厨房"的目标来的，这比品类更贴近他的想法。
            */}
            <p className="mb-2.5 text-[13px] font-medium">空间</p>
            <div className="flex flex-wrap gap-1.5">
              {spaces.map((sp) => {
                const active = search.space === sp.key;
                return (
                  <button
                    key={sp.key}
                    onClick={() => setSearch({ space: active ? undefined : sp.key })}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-[13px] transition-colors",
                      active
                        ? "border-brand-500 bg-brand-50 text-brand-700"
                        : "border-border text-muted-foreground hover:border-brand-200 hover:text-foreground",
                    )}
                  >
                    {sp.label}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* ── 右：结果 ── */}
        <div className="min-w-0">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-[14px] text-muted-foreground">
                {isPending ? "查询中…" : `共 ${data?.items.length ?? 0} 件`}
              </span>
              {/*
                🔴 **生效中的筛选要能一键去掉。**
                   左边栏一眼看不出"我现在筛了什么"（尤其滚下去之后），
                   而网页上用户常常忘了自己勾过关键词，然后以为"这个品类没货"。
              */}
              {chips.map((c) => (
                <button
                  key={c.label}
                  onClick={c.clear}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 pr-1.5 pl-2.5 text-[13px] text-brand-700 transition-colors hover:bg-brand-100"
                >
                  {c.label}
                  <X className="size-3.5" />
                </button>
              ))}
            </div>

            {/* ⚠️ 排序在网页上用下拉：四个并排的按钮占掉一整行，而它不是主操作。 */}
            <select
              className="h-9 shrink-0 rounded-lg border border-border bg-card px-3 text-[14px] outline-none focus:border-brand-500"
              value={search.sort ?? "newest"}
              onChange={(e) => setSearch({ sort: e.target.value })}
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {isPending ? (
            <Spinner compact />
          ) : error ? (
            <ErrorBlock error={error} onRetry={refetch} />
          ) : data.items.length === 0 ? (
            <Empty title="没有找到商品" hint="换个品类或者去掉几个筛选条件试试" compact />
          ) : (
            /* ⚠️ 四列等高网格 —— 网页版的价值就是能并排比较。 */
            <div className="grid grid-cols-4 gap-5">
              {data.items.map((p) => (
                <DesktopProductCard key={p.id} p={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DesktopShell>
  );
}
